import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ClearIcon from '@mui/icons-material/Clear';

import { useApp } from 'contexts/AppContext';
import { useTheme } from 'contexts/ThemeContext';

import { countMarkedForDelete, getKeptListFieldValues } from 'utils/functions';
import pluralize from 'utils/pluralize';
import { INSIGHT, USERS } from 'utils/rest';

import { Text } from 'components/DataDisplay';
import { Modal } from 'components/Feedback';
import { FormSection } from 'components/FormSection';
import {
  Button,
  LocationSearchInput,
  TextField,
  ZdcInput
} from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { ListField } from 'components/ListField';
import { Loading } from 'components/Loading';

const ErrorState = {
  nameError: false,
  keyError: false,
  monthlyBudgetError: false,
  subventionError: false
};
export const ModalForm = props => {
  const { config, onChange, shopGroupName, ...rest } = props;
  const { selected, shopGroups: allShopGroups } = config;

  const [userList, setUserList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [insigtShop, setInsightShop] = useState(null);
  const [canReset, setCanReset] = useState(false);
  const [zdc, setZdc] = useState(null);
  const { isSimplify } = useApp();
  const { themeColors } = useTheme();

  const timerRef = useRef(null);

  const [
    { key, name, siteUri, users, shopGroups, filters, lat, lng, address = '' },
    setState
  ] = React.useState({});
  const [
    { nameError, keyError, latError, lngError },
    setError
  ] = React.useState(ErrorState);

  const toggleError = (keyToggleError, state) => {
    setError(prevState => ({
      ...prevState,
      [`${keyToggleError}Error`]: state
    }));
  };

  const handleChange = itemKey => value => {
    const text = typeof value === 'string' ? value.trim() : value;
    toggleError(itemKey, !text);
    setState(prevState => {
      const states = { ...prevState };
      states[itemKey] = text;
      return states;
    });
  };

  const handleChangeAddress = value => {
    const same =
      value.lat === insigtShop.origin.coordinates[1] &&
      value.lng === insigtShop.origin.coordinates[0] &&
      value.address === insigtShop.address;
    setCanReset(!same);
    setState(prevState => {
      const states = { ...prevState };
      states.lat = value.lat;
      states.lng = value.lng;
      states.address = value.address;
      return states;
    });
  };

  const resetAddress = () => {
    handleChangeAddress({
      lat: insigtShop.origin.coordinates[1],
      lng: insigtShop.origin.coordinates[0],
      address: insigtShop.address
    });
  };

  const onZdcChanged = values => {
    setZdc(values);
  };

  const onValidate = () => {
    let error = false;

    if (!key) {
      toggleError('key', true);
      error = true;
    }
    if (!name) {
      toggleError('name', true);
      error = true;
    }

    if (!error) {
      const datas = {
        key,
        name,
        siteUri,
        users: getKeptListFieldValues(users, selected?.users).map(
          user => user._id
        ),
        filters,
        insigtShop: { original: insigtShop, lat, lng, address, zdc }
      };
      onChange(datas, getKeptListFieldValues(shopGroups));
    }
  };

  const getActions = () => {
    const markedCount = countMarkedForDelete([users, shopGroups]);
    return (
      <Container>
        <Row spacing={1}>
          <Item xs justify="flex-end">
            {markedCount > 0 && (
              <Text
                color={themeColors.designSystem.red}
                style={{ paddingRight: '16px' }}
              >
                {markedCount} {pluralize('élément', markedCount)} à supprimer
              </Text>
            )}
            <Button
              variant="contained"
              size="medium"
              widthSize="medium"
              onClick={onValidate}
            >
              {selected?._id ? 'Sauvegarder' : 'Créer'}
            </Button>
          </Item>
        </Row>
      </Container>
    );
  };

  const fetchUsers = async (search, adminOnly) => {
    const body = { search };
    if (adminOnly) {
      body.roles = ['ADMIN'];
    }
    const found = await USERS.getPaginated(body);
    if (found?.result) {
      setUserList(found.result);
    }
  };

  const pollUsers = (query, adminOnly) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      fetchUsers(query, adminOnly);
    }, 500);
  };

  const loadData = async () => {
    const [currentUsers, insightShops] = await Promise.all([
      USERS.getByIds(selected?.users || []),
      INSIGHT.getInsightShopByIds({ ids: [selected.insightShop], light: true }),
      fetchUsers('')
    ]);
    const { address: shopAddress, origin } = insightShops[0] || {};

    const tmpShopGroup = allShopGroups.filter(el =>
      el.shops.find(e => e === selected?._id)
    );

    const initialState = {
      key: selected?.key || '',
      name: selected?.name || '',
      create: !!selected?.create,
      users:
        selected?.users?.reduce((acc, userId) => {
          const user = currentUsers.find(el => el._id === userId);
          if (user) {
            acc.push({
              email: user.email,
              _id: user._id
            });
          }
          return acc;
        }, []) || [],
      shopGroups: tmpShopGroup.map(el => ({
        name: el.name,
        value: el._id
      })),
      filters: selected?.filters || [],
      address: shopAddress,
      lat: origin?.coordinates?.[1],
      lng: origin?.coordinates?.[0]
    };
    const newShop = insightShops[0] || {};
    setInsightShop(newShop);
    setZdc(newShop);
    setState(initialState);
    setLoaded(true);
  };

  useEffect(() => {
    if (!loaded) {
      loadData();
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Modal actions={getActions()} {...rest}>
      {!loaded ? (
        <Loading />
      ) : (
        <Container>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Point de vente" xs={3}>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <TextField
                            title="Key"
                            small
                            required
                            placeholder="Identifiant unique du magasin"
                            onChange={handleChange('key')}
                            value={key}
                            error={keyError}
                            disabled
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <TextField
                            title="Nom du point de vente"
                            small
                            required
                            onChange={handleChange('name')}
                            value={name}
                            error={nameError}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <TextField
                            title="Url du site"
                            small
                            onChange={handleChange('siteUri')}
                            value={siteUri}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                </Row>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title={shopGroupName} xs={3}>
                <Row spacing={0} justify="flex-start">
                  <Item justify="flex-start">
                    <ListField
                      options={allShopGroups.map(e => ({
                        name: e.name,
                        value: e._id
                      }))}
                      searchOptionsKeys={['name']}
                      values={shopGroups}
                      onChange={handleChange('shopGroups')}
                      actionTxt={`Ajouter un ${shopGroupName.toLowerCase()}`}
                      deleteAction
                      markDelete
                    />
                  </Item>
                </Row>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Utilisateurs" xs={3}>
                <Row spacing={0} justify="flex-start">
                  <Item justify="flex-start">
                    <ListField
                      options={userList.map(e => ({
                        email: e.email,
                        _id: e._id
                      }))}
                      searchOptionsKeys={['email']}
                      values={users}
                      onChange={handleChange('users')}
                      actionTxt="Ajouter un utilisateur"
                      deleteAction
                      markDelete
                      variant="asyncAutoComplete"
                      onChangeInput={value => {
                        pollUsers(value);
                      }}
                    />
                  </Item>
                </Row>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Adresse" xs={3}>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <TextField
                            title="Latitude"
                            small
                            required
                            onChange={handleChange('lat')}
                            value={lat}
                            error={latError}
                            disabled={isSimplify}
                            format="numberDecSigned"
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <TextField
                            title="Longitude"
                            small
                            required
                            onChange={handleChange('lng')}
                            value={lng}
                            error={lngError}
                            disabled={isSimplify}
                            format="numberDecSigned"
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start" alignItems="flex-end">
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <LocationSearchInput
                          title="Adresse"
                          adr={address}
                          onPlaceSelected={handleChangeAddress}
                        />
                      </Item>
                    </Row>
                  </Item>
                  <Item xs={6} justify="flex-start" alignItems="flex-end">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <Button
                          startIcon={<ClearIcon />}
                          variant="contained"
                          disabled={!canReset}
                          onClick={resetAddress}
                        >
                          Réinitialisation
                        </Button>
                      </Item>
                    </Row>
                  </Item>
                </Row>
              </FormSection>
            </Item>
          </Row>
          {!isSimplify && (
            <Row spacing={1}>
              <Item justify="flex-start">
                <FormSection title="Zone de chalandise" xs={3}>
                  <Container>
                    <Row spacing={0} justify="flex-start">
                      <Item justify="flex-start">
                        <ZdcInput value={zdc} onChange={onZdcChanged} />
                      </Item>
                    </Row>
                  </Container>
                </FormSection>
              </Item>
            </Row>
          )}
          {!isSimplify && (
            <Row spacing={1}>
              <Item justify="flex-start">
                <FormSection title="Filtres" xs={3}>
                  <Container>
                    <Row spacing={0} justify="flex-start">
                      <Item justify="flex-start">
                        <Text>
                          Les filtres peuvent être utilisés pour trier les
                          magasins ainsi que leurs campagnes lors d’un lancement
                          de campagne ou dans le funnel agrégé. Sélectionnez ici
                          des filtres pour votre point de vente, ou écrivez-en
                          de nouveau puis appuyez sur <strong>Entrée</strong>.
                        </Text>
                      </Item>
                      <Item justify="flex-start">
                        <ListField
                          options={['key', 'value']}
                          keyEnter
                          inputs={['TextField', 'TextField']}
                          labels={['key', 'value']}
                          isRequireds={[true, true]}
                          editAction
                          variant="customFields"
                          onChange={handleChange('filters')}
                          actionTxt="Ajouter un filtre"
                          values={filters}
                        />
                      </Item>
                    </Row>
                  </Container>
                </FormSection>
              </Item>
            </Row>
          )}
        </Container>
      )}
    </Modal>
  );
};

ModalForm.defaultProps = {
  onClose: () => null
};
ModalForm.propTypes = {
  onCreate: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  config: PropTypes.objectOf(PropTypes.any).isRequired
};

export default ModalForm;
